import ReadOnlyMessageText from './ReadOnlyMessageText';
import { decodeDisplayText } from '../../util/displayText';
import '../../i18n';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FiExternalLink } from 'react-icons/fi';

import { PALE_WHITE_1, PALE_WHITE_6 } from '../../appearance/Colors';
import ReadOnlyUrlMetaPreview from './ReadOnlyUrlMetaPreview';
import ReadOnlyFilePreview from './ReadOnlyFilePreview';
import ReadOnlyMessageActions from './ReadOnlyMessageActions';
import ReadOnlyMessageImage from './ReadOnlyMessageImage';

const formatMessageTime = createdAt => {
    if (!createdAt) {
        return '';
    }

    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

const getSourceLabel = (message, sourceLink) => {
    if (message.sourceHost) {
        return message.sourceHost;
    }

    if (!sourceLink) {
        return '';
    }

    try {
        return new URL(sourceLink).hostname;
    } catch (error) {
        return sourceLink;
    }
};

const getMessageTimestamp = message => {
    const date = new Date(message?.createdAt);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const LOAD_MORE_THRESHOLD_PX = 320;

const ReadOnlyMessageChat = ({
    messages,
    onSelectMessage,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    bottomUp = false,
    preserveOrder = false,
    onSaveMessage = null,
    savingMessageId = null,
    onActionStatus = null
}) => {
    const { t: lnT } = useTranslation();

    const viewportRef = useRef(null);
    const preserveScrollRef = useRef(null);
    const hasAnchoredToBottomRef = useRef(false);
    const isNearBottomRef = useRef(true);
    const orderedMessages = useMemo(
        () =>
            preserveOrder
                ? [...messages]
                : [...messages].sort((firstMessage, secondMessage) =>
                      bottomUp
                          ? getMessageTimestamp(firstMessage) - getMessageTimestamp(secondMessage)
                          : getMessageTimestamp(secondMessage) - getMessageTimestamp(firstMessage)
                  ),
        [lnT, bottomUp, messages, preserveOrder]
    );

    const updateNearBottom = viewport => {
        if (!viewport) {
            return;
        }

        const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
        isNearBottomRef.current = distanceFromBottom < LOAD_MORE_THRESHOLD_PX;
    };

    const maybeLoadMore = () => {
        const viewport = viewportRef.current;
        if (!viewport || !hasMore || loadingMore || typeof onLoadMore !== 'function') {
            return;
        }

        updateNearBottom(viewport);
        if (bottomUp) {
            if (viewport.scrollTop < LOAD_MORE_THRESHOLD_PX) {
                preserveScrollRef.current = {
                    scrollHeight: viewport.scrollHeight,
                    scrollTop: viewport.scrollTop
                };
                onLoadMore();
            }
            return;
        }

        const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
        if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
            onLoadMore();
        }
    };

    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || !bottomUp) {
            return;
        }

        const preservedScroll = preserveScrollRef.current;
        if (preservedScroll) {
            const heightDelta = viewport.scrollHeight - preservedScroll.scrollHeight;
            viewport.scrollTop = preservedScroll.scrollTop + heightDelta;
            preserveScrollRef.current = null;
            updateNearBottom(viewport);
            return;
        }

        if (!hasAnchoredToBottomRef.current || isNearBottomRef.current) {
            viewport.scrollTop = viewport.scrollHeight;
            hasAnchoredToBottomRef.current = true;
            updateNearBottom(viewport);
        }
    }, [bottomUp, orderedMessages.length]);

    useEffect(() => {
        maybeLoadMore();
    }, [hasMore, loadingMore, orderedMessages.length]);

    return (
        <ChatViewport ref={viewportRef} onScroll={maybeLoadMore}>
            <ChatList $bottomUp={bottomUp} aria-label={lnT('messages:publishedFolderChat')}>
                {bottomUp && loadingMore ? (
                    <LoadingMoreText>{lnT('folders:loadingOlderMessages')}</LoadingMoreText>
                ) : null}
                {orderedMessages.map(message => {
                    const urlMeta = message.urlMeta?.[0];
                    const sourceLink = message.canonicalSourceUrls?.[0] || message.sourceUrls?.[0] || urlMeta?.url;
                    const sourceLabel = getSourceLabel(message, sourceLink);
                    const bodyText = decodeDisplayText(message.body);
                    const title = decodeDisplayText(message.title || urlMeta?.title);
                    const timeLabel = formatMessageTime(message.createdAt);
                    const shouldShowSourceMeta = !urlMeta && (sourceLink || sourceLabel);
                    const isSavingMessage = savingMessageId === message.id;

                    return (
                        <MessageContainer key={message.id} id={message.id}>
                            <MessageWrapper>
                                <MessageDiv
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onSelectMessage?.(message)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            onSelectMessage?.(message);
                                        }
                                    }}
                                    aria-label={
                                        title
                                            ? lnT('auth:open', { value1: String(title) })
                                            : lnT('messages:openSharedMessage')
                                    }
                                >
                                    <MessageContent>
                                        {message.image ? (
                                            <MessageImage image={message.image} alt={title || ''} loading="lazy" />
                                        ) : null}

                                        <ReadOnlyFilePreview file={message.file} />

                                        {urlMeta ? (
                                            <UrlPreviewSpacing>
                                                <ReadOnlyUrlMetaPreview
                                                    urlMeta={urlMeta}
                                                    sourceHost={message.sourceHost}
                                                    variant="full"
                                                    message={message}
                                                    index={0}
                                                />
                                            </UrlPreviewSpacing>
                                        ) : null}

                                        {title && title !== decodeDisplayText(urlMeta?.title) ? (
                                            <PreMessage><ReadOnlyMessageText text={title}/></PreMessage>
                                        ) : null}
                                        {bodyText ? <BodyText><ReadOnlyMessageText text={bodyText}/></BodyText> : null}

                                        {shouldShowSourceMeta || timeLabel ? (
                                            <MessageMetaRow>
                                                <MessageMetaLeftGroup>
                                                    {shouldShowSourceMeta && sourceLink ? (
                                                        <SourceLink
                                                            href={sourceLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={event => event.stopPropagation()}
                                                        >
                                                            <FiExternalLink size={12} />
                                                            {sourceLabel}
                                                        </SourceLink>
                                                    ) : shouldShowSourceMeta && sourceLabel ? (
                                                        <Source>{sourceLabel}</Source>
                                                    ) : null}
                                                </MessageMetaLeftGroup>
                                                {timeLabel ? <MessageMetaTime>{timeLabel}</MessageMetaTime> : null}
                                            </MessageMetaRow>
                                        ) : null}
                                    </MessageContent>
                                </MessageDiv>
                            </MessageWrapper>
                            <ReadOnlyMessageActions
                                message={message}
                                onSaveMessage={onSaveMessage}
                                saving={isSavingMessage}
                                onStatus={onActionStatus}
                                placement="inline"
                            />
                        </MessageContainer>
                    );
                })}
                {!bottomUp && loadingMore ? <LoadingMoreText>{lnT('common:loading')}</LoadingMoreText> : null}
            </ChatList>
        </ChatViewport>
    );
};

const ChatViewport = styled.div`
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 4px 18px 0;
    scrollbar-gutter: stable;

    @media (max-width: 560px) {
        padding-right: 0;
    }
`;

const ChatList = styled.div`
    min-height: ${props => (props.$bottomUp ? '100%' : 'auto')};
    justify-content: ${props => (props.$bottomUp ? 'flex-end' : 'flex-start')};
    > * {
        flex: ${props => (props.$bottomUp ? '0 0 auto' : '1')};
    }
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    align-items: flex-start;
`;

const LoadingMoreText = styled.div`
    align-self: center;
    color: ${PALE_WHITE_6};
    font-size: 12px;
    padding: 10px 0;
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex: 1;
    width: 100%;
    position: relative;
    overflow: visible;
`;

const MessageWrapper = styled.div`
    position: relative;
    overflow: hidden;
    isolation: isolate;
    contain: paint;
    display: flex;
    flex-direction: row;
    min-width: 190px;
    width: auto;
    max-width: 500px;
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.9);

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        clip-path: inset(0 round 8px);
        background-color: rgba(35, 39, 42, 0.72);
        backdrop-filter: saturate(160%) blur(8px);
        -webkit-backdrop-filter: saturate(160%) blur(8px);
        pointer-events: none;
        z-index: 0;
    }

    @media (max-width: 560px) {
        max-width: calc(100vw - 58px);
    }
`;

const MessageDiv = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    padding: 8px;
    cursor: pointer;
    width: 100%;
    word-break: break-word;
    user-select: text;
    position: relative;
    z-index: 1;
    overflow-x: hidden;
    overflow-y: visible;

    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
        border-radius: 8px;
    }
`;

const MessageContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
`;

const MessageImage = styled(ReadOnlyMessageImage)`
    width: 100%;
    max-width: 100%;
    max-height: 360px;
    object-fit: cover;
    display: block;
    border-radius: 6px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.06);
`;

const UrlPreviewSpacing = styled.div`
    margin-bottom: 8px;
`;

const PreMessage = styled.div`
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 150%;
    overflow-wrap: break-word;
    max-width: 100%;
    color: inherit;
    font-size: 14px;
    margin-bottom: 4px;
`;

const BodyText = styled.div`
    max-height: 14.5em;
    overflow: hidden;
    padding: 1px;
    white-space: pre-wrap;
    word-break: break-word;
    color: rgba(255, 255, 255, 0.78);
    font-size: 14px;
    line-height: 150%;
`;

const MessageMetaRow = styled.div`
    color: inherit;
    opacity: 0.72;
    padding-top: 8px;
    padding-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    min-width: 0;
    width: 100%;
    flex-wrap: nowrap;
    border-radius: 6px;
`;

const MessageMetaLeftGroup = styled.div`
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MessageMetaTime = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    color: ${PALE_WHITE_6};
`;

const Source = styled.div`
    min-width: 0;
    font-size: inherit;
    color: ${PALE_WHITE_6};
    overflow-wrap: anywhere;
`;

const SourceLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    width: fit-content;
    color: #2b88d9;
    font-size: inherit;
    font-weight: 600;
    text-decoration: none;
    overflow-wrap: anywhere;

    &:hover {
        text-decoration: underline;
    }
`;

export default ReadOnlyMessageChat;
